import React, { useState, useEffect } from 'react';
import { ClipboardList, CheckCircle, Clock, DollarSign, Edit2, Save, X, Filter, Search, TrendingUp, Building2, ChevronDown, ChevronUp, FileCheck, FileX, Calendar, Ban, RefreshCw, Trash2, RotateCcw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const TransactionTracking = () => {
  const [transactions, setTransactions] = useState([]);
  const [deletedTransactions, setDeletedTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // pending, paid, deleted
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [paymentModal, setPaymentModal] = useState({ show: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [paymentData, setPaymentData] = useState({
    payment_method: '',
    payment_notes: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  
  // Collapsible sections - dynamically populated
  const [expandedCategories, setExpandedCategories] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transRes, settingsRes, deletedRes] = await Promise.all([
        api.get('/transaction-tracking'),
        api.get('/settings'),
        api.get('/transaction-tracking/deleted/list').catch(() => ({ data: [] }))
      ]);
      setTransactions(transRes.data);
      setSettings(settingsRes.data);
      setDeletedTransactions(deletedRes.data || []);
      
      // Initialize expanded categories
      const customCategories = settingsRes.data?.service_categories || [];
      const allCatIds = ['official', 'agency', 'other', ...customCategories.map(c => c.id)];
      const expandedInit = {};
      allCatIds.forEach(id => expandedInit[id] = true);
      setExpandedCategories(expandedInit);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const [transRes, deletedRes] = await Promise.all([
        api.get('/transaction-tracking'),
        api.get('/transaction-tracking/deleted/list').catch(() => ({ data: [] }))
      ]);
      setTransactions(transRes.data);
      setDeletedTransactions(deletedRes.data || []);
    } catch (error) {
      toast.error('İşlemler yüklenemedi');
      console.error(error);
    }
  };

  // Yenile butonu - Ayarlardan senkronize et
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      // Backend'de senkronizasyon endpoint'ini çağır
      await api.post('/transaction-tracking/sync-from-settings');
      // Verileri yeniden yükle
      await loadData();
      toast.success('Veriler güncellendi');
    } catch (error) {
      toast.error('Yenileme başarısız');
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      service_name: item.service_name,
      supplier: item.supplier || '',
      cost_price: item.cost_price || 0,
      selling_price: item.selling_price || 0,
      category: item.category
    });
  };

  const handleSaveEdit = async (itemId) => {
    try {
      // Sadece değişen alanları gönder
      const updatePayload = {
        service_name: editData.service_name,
        supplier: editData.supplier,
        cost_price: parseFloat(editData.cost_price) || 0,
        selling_price: parseFloat(editData.selling_price) || 0,
        category: editData.category
      };
      console.log('Saving transaction:', itemId, updatePayload);
      await api.put(`/transaction-tracking/${itemId}`, updatePayload);
      toast.success('İşlem güncellendi');
      setEditingId(null);
      loadTransactions();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Güncelleme başarısız');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const openPaymentModal = (item) => {
    setPaymentModal({ show: true, item });
    setPaymentData({
      payment_method: '',
      payment_notes: '',
      payment_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleMarkPaid = async () => {
    if (!paymentModal.item) return;
    
    try {
      await api.put(`/transaction-tracking/${paymentModal.item.id}/mark-paid`, {
        payment_method: paymentData.payment_method,
        payment_notes: paymentData.payment_notes,
        payment_date: paymentData.payment_date
      });
      toast.success('İşlem ödendi olarak işaretlendi');
      setPaymentModal({ show: false, item: null });
      loadTransactions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleMarkPending = async (itemId) => {
    try {
      await api.put(`/transaction-tracking/${itemId}/mark-pending`);
      toast.success('İşlem bekleyen durumuna alındı');
      loadTransactions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  // Silme işlemi
  const handleDelete = async () => {
    if (!deleteModal.item) return;
    
    try {
      await api.delete(`/transaction-tracking/${deleteModal.item.id}`);
      toast.success('İşlem kaydı silindi. Silinmişler sekmesinden geri getirebilirsiniz.');
      setDeleteModal({ show: false, item: null });
      loadTransactions();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  // Geri getirme işlemi
  const handleRestore = async (itemId) => {
    try {
      await api.post(`/transaction-tracking/${itemId}/restore`);
      toast.success('İşlem kaydı geri getirildi');
      loadTransactions();
    } catch (error) {
      toast.error('Geri getirme işlemi başarısız');
    }
  };

  // Kalıcı silme
  const handlePermanentDelete = async (itemId) => {
    if (!window.confirm('Bu kayıt kalıcı olarak silinecek ve geri getirilemeyecek. Emin misiniz?')) {
      return;
    }
    
    try {
      await api.delete(`/transaction-tracking/${itemId}/permanent`);
      toast.success('İşlem kaydı kalıcı olarak silindi');
      loadTransactions();
    } catch (error) {
      toast.error('Kalıcı silme işlemi başarısız');
    }
  };

  const handleInvoiceStatusChange = async (itemId, newStatus) => {
    try {
      await api.put(`/transaction-tracking/${itemId}/invoice-status?invoice_status=${newStatus}`);
      const messages = {
        'pending': 'Fatura bekliyor olarak işaretlendi',
        'received': 'Fatura geldi olarak işaretlendi',
        'not_required': 'Giriş faturası yok olarak işaretlendi'
      };
      toast.success(messages[newStatus] || 'Fatura durumu güncellendi');
      loadTransactions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  // Fatura durumu için bir sonraki durumu döndür (döngüsel)
  const getNextInvoiceStatus = (currentStatus) => {
    // Eski kayıtlar için invoice_received alanını kontrol et
    if (currentStatus === undefined || currentStatus === null) {
      return 'pending';
    }
    const cycle = {
      'pending': 'received',
      'received': 'not_required',
      'not_required': 'pending'
    };
    return cycle[currentStatus] || 'pending';
  };

  // Fatura durumu için görünüm bilgilerini döndür
  const getInvoiceStatusDisplay = (status) => {
    // Eski kayıtlar için invoice_received alanını kontrol et
    const displays = {
      'pending': { label: 'Bekliyor', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200', icon: 'FileX' },
      'received': { label: 'Geldi', color: 'bg-green-100 text-green-700 hover:bg-green-200', icon: 'FileCheck' },
      'not_required': { label: 'Fatura Yok', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', icon: 'FileOff' }
    };
    return displays[status] || displays['pending'];
  };

  // Bekleme günü hesapla
  const calculateDaysWaiting = (serviceDateStr) => {
    if (!serviceDateStr) return 0;
    const serviceDate = new Date(serviceDateStr);
    const today = new Date();
    const diffTime = Math.abs(today - serviceDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filtreleme
  const filteredTransactions = transactions.filter(t => {
    const matchesStatus = activeTab === 'pending' ? t.status === 'pending' : t.status === 'paid';
    const matchesSearch = t.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.vessel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesStatus && matchesSearch && matchesCategory;
  });

  // Kategoriler - Settings'den al + varsayılan kategorileri ekle
  const settingsCategories = settings?.service_categories || [];
  
  // Varsayılan kategoriler - her zaman mevcut olmalı
  const defaultCategories = [
    { id: 'official', name: 'Resmi Masraflar' },
    { id: 'agency', name: 'Acentelik Hizmetleri' },
    { id: 'other', name: 'Diğer' }
  ];
  
  // Settings kategorilerini ve varsayılanları birleştir (duplicate olmaması için)
  const existingIds = new Set(settingsCategories.map(c => c.id));
  const allCategories = [
    ...settingsCategories,
    ...defaultCategories.filter(dc => !existingIds.has(dc.id))
  ];

  // Kategoriye göre grupla - dinamik
  const groupedTransactions = {};
  allCategories.forEach(cat => {
    groupedTransactions[cat.id] = filteredTransactions.filter(t => t.category === cat.id);
  });
  // "other" kategorisine atanmamış işlemleri de ekle
  const assignedCategoryIds = allCategories.map(c => c.id);
  const uncategorized = filteredTransactions.filter(t => !assignedCategoryIds.includes(t.category));
  if (uncategorized.length > 0) {
    groupedTransactions['other'] = [...(groupedTransactions['other'] || []), ...uncategorized];
  }

  // Kategori adlarını dinamik olarak oluştur
  const categoryNames = {};
  allCategories.forEach(cat => {
    categoryNames[cat.id] = cat.name;
  });

  // Renkleri dinamik olarak oluştur
  const colorPalette = [
    { bg: 'bg-blue-50 border-blue-200', header: 'bg-blue-100 text-blue-800' },
    { bg: 'bg-green-50 border-green-200', header: 'bg-green-100 text-green-800' },
    { bg: 'bg-gray-50 border-gray-200', header: 'bg-gray-100 text-gray-800' },
    { bg: 'bg-purple-50 border-purple-200', header: 'bg-purple-100 text-purple-800' },
    { bg: 'bg-orange-50 border-orange-200', header: 'bg-orange-100 text-orange-800' },
    { bg: 'bg-pink-50 border-pink-200', header: 'bg-pink-100 text-pink-800' },
    { bg: 'bg-teal-50 border-teal-200', header: 'bg-teal-100 text-teal-800' },
    { bg: 'bg-indigo-50 border-indigo-200', header: 'bg-indigo-100 text-indigo-800' },
  ];

  const categoryColors = {};
  const categoryHeaderColors = {};
  allCategories.forEach((cat, idx) => {
    const color = colorPalette[idx % colorPalette.length];
    categoryColors[cat.id] = color.bg;
    categoryHeaderColors[cat.id] = color.header;
  });

  // Özet hesaplamaları
  const pendingItems = transactions.filter(t => t.status === 'pending');
  const paidItems = transactions.filter(t => t.status === 'paid');

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transaction-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">İşlem Takip</h1>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Yenileniyor...' : 'Yenile'}
        </button>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-2">
            <Clock className="h-5 w-5" />
            <span className="text-sm font-medium">Bekleyen</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{pendingItems.length}</div>
          <div className="text-sm text-gray-500">işlem bekliyor</div>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Ödenen</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{paidItems.length}</div>
          <div className="text-sm text-gray-500">işlem tamamlandı</div>
        </div>
      </div>

      {/* Sekmeler ve Filtreler */}
      <div className="bg-white rounded-lg border">
        <div className="border-b px-4">
          <div className="flex flex-wrap items-center justify-between gap-4 py-4">
            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'pending'
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="pending-tab"
              >
                <Clock className="h-4 w-4 inline mr-2" />
                Bekleyen ({pendingItems.length})
              </button>
              <button
                onClick={() => setActiveTab('paid')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'paid'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="paid-tab"
              >
                <CheckCircle className="h-4 w-4 inline mr-2" />
                Ödenen ({paidItems.length})
              </button>
              {deletedTransactions.length > 0 && (
                <button
                  onClick={() => setActiveTab('deleted')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'deleted'
                      ? 'bg-red-100 text-red-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="deleted-tab"
                >
                  <Trash2 className="h-4 w-4 inline mr-2" />
                  Silinmişler ({deletedTransactions.length})
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-input"
                />
              </div>
              
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="category-filter"
              >
                <option value="all">Tüm Kategoriler</option>
                {allCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* İşlem Listesi - Kategoriye Göre Gruplandırılmış */}
        {activeTab !== 'deleted' && (
        <div className="p-4 space-y-4">
          {allCategories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Henüz kategori tanımlanmamış</p>
            </div>
          ) : (
            allCategories.map((cat, catIndex) => {
              const items = groupedTransactions[cat.id] || [];
              
              // Boş kategorileri gösterme
              if (items.length === 0) return null;
              
              const categoryTotal = items.reduce((sum, t) => sum + (t.selling_price || 0), 0);
              const categoryCost = items.reduce((sum, t) => sum + (t.cost_price || 0), 0);
              const categoryProfit = categoryTotal - categoryCost;
              
              // Renkleri belirle
              const colors = colorPalette[catIndex % colorPalette.length];
              
              return (
                <div key={cat.id} className={`rounded-lg border ${colors.bg}`}>
                  {/* Kategori Başlığı */}
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    className={`w-full px-4 py-3 flex items-center justify-between ${colors.header} rounded-t-lg`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5" />
                      <span className="font-semibold">{cat.name}</span>
                      <span className="text-sm opacity-75">({items.length} işlem)</span>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Ödenecek veya Ödenen Toplam */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs opacity-75">{activeTab === 'pending' ? 'Ödenecek Toplam' : 'Ödenen Toplam'}</div>
                          <div className="text-lg font-bold">{formatCurrency(categoryTotal)}</div>
                        </div>
                        <div className="text-right border-l pl-3 opacity-80">
                          <div className="text-xs opacity-75">Maliyet</div>
                          <div className="text-sm font-semibold">{formatCurrency(categoryCost)}</div>
                        </div>
                        <div className="text-right border-l pl-3">
                          <div className="text-xs opacity-75">Kar</div>
                          <div className={`text-sm font-bold ${categoryProfit >= 0 ? '' : 'text-red-700'}`}>
                            {formatCurrency(categoryProfit)}
                          </div>
                        </div>
                      </div>
                      {expandedCategories[cat.id] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </button>
                  
                  {/* Kategori İçeriği */}
                  {expandedCategories[cat.id] && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/50">
                          <tr className="text-left text-sm text-gray-600">
                            <th className="px-4 py-3 font-medium">Tarih</th>
                            <th className="px-4 py-3 font-medium">Fatura</th>
                            <th className="px-4 py-3 font-medium">Gemi / Müşteri</th>
                            <th className="px-4 py-3 font-medium">Hizmet Kalemi</th>
                            <th className="px-4 py-3 font-medium">Tedarikçi</th>
                            <th className="px-4 py-3 font-medium text-center">Giriş Faturası</th>
                            <th className="px-4 py-3 font-medium text-right">Alış Fiyatı</th>
                            <th className="px-4 py-3 font-medium text-right">Satış Fiyatı</th>
                            <th className="px-4 py-3 font-medium text-right">Kar</th>
                            {activeTab === 'pending' && <th className="px-4 py-3 font-medium text-center">Bekleme</th>}
                            {activeTab === 'paid' && <th className="px-4 py-3 font-medium">Ödeme Detayı</th>}
                            <th className="px-4 py-3 font-medium text-center">İşlem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {items.map((item) => {
                            const daysWaiting = calculateDaysWaiting(item.service_date);
                            return (
                            <tr key={item.id} className="bg-white hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{formatDate(item.service_date)}</td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium text-blue-600">{item.invoice_number}</span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium">{item.vessel_name || '-'}</div>
                                <div className="text-xs text-gray-500">{item.client_name}</div>
                              </td>
                              <td className="px-4 py-3">
                                {editingId === item.id ? (
                                  <input
                                    type="text"
                                  value={editData.service_name}
                                    onChange={(e) => setEditData({ ...editData, service_name: e.target.value })}
                                    className="w-full px-2 py-1 border rounded text-sm"
                                  />
                                ) : (
                                  <span className="text-sm">{item.service_name}</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {editingId === item.id ? (
                                  <input
                                    type="text"
                                    value={editData.supplier}
                                    onChange={(e) => setEditData({ ...editData, supplier: e.target.value })}
                                    className="w-full px-2 py-1 border rounded text-sm"
                                    placeholder="Tedarikçi"
                                  />
                                ) : (
                                  <span className="text-sm text-gray-600">{item.supplier || '-'}</span>
                                )}
                              </td>
                              {/* Giriş Faturası Durumu */}
                              <td className="px-4 py-3 text-center">
                                {(() => {
                                  const currentStatus = item.invoice_status || 'pending';
                                  const display = getInvoiceStatusDisplay(currentStatus);
                                  const nextStatus = getNextInvoiceStatus(currentStatus);
                                  return (
                                    <button
                                      onClick={() => handleInvoiceStatusChange(item.id, nextStatus)}
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${display.color}`}
                                      title={`Tıklayarak durumu değiştirin: Bekliyor → Geldi → Fatura Yok → Bekliyor`}
                                      data-testid={`invoice-status-${item.id}`}
                                    >
                                      {currentStatus === 'pending' && <FileX className="h-3.5 w-3.5" />}
                                      {currentStatus === 'received' && <FileCheck className="h-3.5 w-3.5" />}
                                      {currentStatus === 'not_required' && <Ban className="h-3.5 w-3.5" />}
                                      <span>{display.label}</span>
                                    </button>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {editingId === item.id ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editData.cost_price ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setEditData({ ...editData, cost_price: val === '' ? 0 : parseFloat(val) });
                                    }}
                                    className="w-24 px-2 py-1 border rounded text-sm text-right"
                                  />
                                ) : (
                                  <span className="text-sm text-red-600">{formatCurrency(item.cost_price, item.currency)}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {editingId === item.id ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editData.selling_price ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setEditData({ ...editData, selling_price: val === '' ? 0 : parseFloat(val) });
                                    }}
                                    className="w-24 px-2 py-1 border rounded text-sm text-right"
                                  />
                                ) : (
                                  <span className="text-sm text-green-600">{formatCurrency(item.selling_price, item.currency)}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`text-sm font-medium ${(item.profit_margin || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(item.profit_margin, item.currency)}
                                </span>
                                {item.profit_percentage > 0 && (
                                  <span className="text-xs text-gray-500 ml-1">(%{item.profit_percentage})</span>
                                )}
                              </td>
                              {/* Bekleme Süresi - sadece pending tab'da */}
                              {activeTab === 'pending' && (
                                <td className="px-4 py-3 text-center">
                                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    daysWaiting > 30 ? 'bg-red-100 text-red-700' :
                                    daysWaiting > 14 ? 'bg-orange-100 text-orange-700' :
                                    daysWaiting > 7 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span>{daysWaiting} gün</span>
                                  </div>
                                </td>
                              )}
                              {/* Ödeme Detayı - sadece paid tab'da */}
                              {activeTab === 'paid' && (
                                <td className="px-4 py-3 text-sm">
                                  <div className="text-gray-700">{item.payment_method || '-'}</div>
                                  <div className="text-xs text-gray-500">{formatDate(item.payment_date)}</div>
                                  {item.payment_notes && (
                                    <div className="text-xs text-gray-400 mt-1 max-w-[200px] truncate" title={item.payment_notes}>
                                      {item.payment_notes}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  {editingId === item.id ? (
                                    <>
                                      <button
                                        onClick={() => handleSaveEdit(item.id)}
                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                        title="Kaydet"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                        title="İptal"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleEdit(item)}
                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                        title="Düzenle"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </button>
                                      {activeTab === 'pending' ? (
                                        (() => {
                                          // Ödeme için fatura durumu kontrolü
                                          // "received" veya "not_required" durumlarında ödeme yapılabilir
                                          const invoiceStatus = item.invoice_status || 'pending';
                                          const canPay = invoiceStatus === 'received' || invoiceStatus === 'not_required';
                                          const isEditing = editingId === item.id;
                                          const isDisabled = !canPay || isEditing;
                                          
                                          let tooltipText = 'Ödendi İşaretle';
                                          if (isEditing) {
                                            tooltipText = 'Önce değişiklikleri kaydedin';
                                          } else if (!canPay) {
                                            tooltipText = 'Önce fatura durumunu "Geldi" veya "Fatura Yok" olarak işaretleyin';
                                          }
                                          
                                          return (
                                            <button
                                              onClick={() => !isDisabled && openPaymentModal(item)}
                                              className={`p-1 rounded ${
                                                isDisabled 
                                                  ? 'text-gray-300 cursor-not-allowed' 
                                                  : 'text-green-600 hover:bg-green-50'
                                              }`}
                                              title={tooltipText}
                                              disabled={isDisabled}
                                            >
                                              <CheckCircle className="h-4 w-4" />
                                            </button>
                                          );
                                        })()
                                      ) : (
                                        <button
                                          onClick={() => handleMarkPending(item.id)}
                                          className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                                          title="Bekleyene Al"
                                        >
                                          <Clock className="h-4 w-4" />
                                        </button>
                                      )}
                                      {/* Silme butonu */}
                                      <button
                                        onClick={() => setDeleteModal({ show: true, item })}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                        title="Sil"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        )}
      </div>

      {/* Ödeme Modal */}
      {paymentModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 m-4" data-testid="payment-modal">
            <h3 className="text-lg font-semibold mb-4">Ödeme Bilgileri</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hizmet</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {paymentModal.item?.service_name}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tutar</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {formatCurrency(paymentModal.item?.selling_price, paymentModal.item?.currency)}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Tarihi</label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Yöntemi</label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="Nakit">Nakit</option>
                  <option value="Havale/EFT">Havale/EFT</option>
                  <option value="Kredi Kartı">Kredi Kartı</option>
                  <option value="Çek">Çek</option>
                  <option value="Mahsup">Mahsup</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama / Detay</label>
                <textarea
                  value={paymentData.payment_notes}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                  placeholder="Ödeme ile ilgili detayları girin..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setPaymentModal({ show: false, item: null })}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={handleMarkPaid}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                data-testid="confirm-payment-btn"
              >
                Ödendi İşaretle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 m-4" data-testid="delete-modal">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Kaydı Sil</h3>
            
            <div className="space-y-4">
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                <strong>{deleteModal.item?.service_name}</strong>
                <div className="text-gray-600 mt-1">
                  {deleteModal.item?.vessel_name} - {deleteModal.item?.client_name}
                </div>
                <div className="text-gray-600">
                  Tutar: {formatCurrency(deleteModal.item?.selling_price, deleteModal.item?.currency)}
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Bu kaydı silmek istediğinizden emin misiniz? 
                <span className="text-green-600 font-medium"> Silinmişler sekmesinden geri getirebilirsiniz.</span>
              </p>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteModal({ show: false, item: null })}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silinmişler Tablosu */}
      {activeTab === 'deleted' && (
        <div className="bg-white rounded-xl border shadow-sm mt-6">
          <div className="p-4 border-b bg-red-50">
            <h3 className="text-lg font-semibold text-red-700">Silinmiş Kayıtlar</h3>
            <p className="text-sm text-red-600">Bu kayıtlar silinmiş ancak geri getirilebilir.</p>
          </div>
          
          {deletedTransactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Silinmiş kayıt bulunmuyor.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="deleted-table">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tarih</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Hizmet</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Gemi / Müşteri</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Tutar</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Silinme Tarihi</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedTransactions.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.service_date ? new Date(item.service_date).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.service_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div>{item.vessel_name || '-'}</div>
                        <div className="text-xs text-gray-400">{item.client_name || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(item.selling_price, item.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-600">
                        {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleRestore(item.id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Geri Getir"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(item.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Kalıcı Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionTracking;
